/**
 * Button.js — v2
 *
 * Cambios:
 *  1. Nueva prop `className`: aplica la clase CSS original (.btn-view, etc.)
 *     para que el StylesBlock pueda aplicar el estilo correcto.
 *
 *  2. El texto del botón ahora se renderiza como innerHTML cuando contiene HTML
 *     (ej: <span class="material-icons">visibility</span> Ver Canalización).
 *     Esto preserva los iconos dentro del botón.
 *
 *  3. El botón renderiza como <button> nativo (no MUI) para que los estilos
 *     del CSS original aplicados vía className funcionen sin conflicto.
 */
import React from 'react';
import { useNode } from '@craftjs/core';
import { TextField, FormControl, FormLabel, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  settings: { padding: theme.spacing(2) },
  rawStyleTextarea: {
    width: '100%',
    fontFamily: '"Roboto Mono", monospace',
    fontSize: '0.68rem',
    padding: theme.spacing(1),
    border: '1px solid #ccc',
    borderRadius: 4,
    minHeight: 80,
    resize: 'vertical',
    outline: 'none',
    lineHeight: 1.5,
    color: '#333',
    backgroundColor: '#fafafa',
  },
  hint: { fontSize: '0.72rem', color: '#999', marginTop: 4, marginBottom: theme.spacing(1) },
}));

export const Button = ({ text, backgroundColor, textColor, size, className, rawStyle }) => {
  const { connectors: { connect, drag } } = useNode();

  const hasHtml = text && text.includes('<');

  // Los estilos propios del componente (props explícitas) tienen prioridad
  // sobre rawStyle. Si tiene className, el CSS del StylesBlock puede
  // sobreescribir rawStyle con mayor especificidad.
  const style = {
    ...rawStyle,
    ...(backgroundColor ? { backgroundColor } : {}),
    ...(textColor       ? { color: textColor }  : {}),
    cursor: 'pointer',
  };

  return (
    <button
      ref={(ref) => connect(drag(ref))}
      className={className || undefined}
      style={style}
      dangerouslySetInnerHTML={hasHtml ? { __html: text } : undefined}
    >
      {!hasHtml ? text : undefined}
    </button>
  );
};

const ButtonSettings = () => {
  const classes = useStyles();
  const {
    actions: { setProp },
    text, backgroundColor, textColor, size, className, rawStyle,
  } = useNode((node) => ({
    text:            node.data.props.text,
    backgroundColor: node.data.props.backgroundColor,
    textColor:       node.data.props.textColor,
    size:            node.data.props.size,
    className:       node.data.props.className,
    rawStyle:        node.data.props.rawStyle,
  }));

  const rawStyleStr = rawStyle ? JSON.stringify(rawStyle, null, 2) : '{}';

  return (
    <div className={classes.settings}>

      {/* Clase CSS original */}
      {className && (
        <FormControl fullWidth margin="normal">
          <FormLabel>Clase CSS original</FormLabel>
          <TextField
            size="small"
            value={className}
            onChange={(e) => setProp((props) => (props.className = e.target.value))}
            inputProps={{ style: { fontSize: '0.78rem', fontFamily: 'monospace' } }}
          />
        </FormControl>
      )}

      {/* Texto / HTML */}
      <FormControl fullWidth margin="normal">
        <FormLabel>Texto del botón</FormLabel>
        <TextField
          value={text || ''}
          onChange={(e) => setProp((props) => (props.text = e.target.value))}
          multiline rows={2}
          helperText="Puede contener HTML (ej: <span class='material-icons'>send</span> Enviar)"
        />
      </FormControl>

      {/* Color de fondo */}
      <FormControl fullWidth margin="normal">
        <FormLabel>Color de fondo</FormLabel>
        <TextField type="color"
          value={(() => {
            if (!backgroundColor || backgroundColor === 'transparent') return '#00b31f';
            if (backgroundColor.startsWith('#')) return backgroundColor;
            const m = backgroundColor.match(/rgba?\(\s*(\d+),\s*(\d+),\s*(\d+)/);
            if (m) return `#${parseInt(m[1]).toString(16).padStart(2,'0')}${parseInt(m[2]).toString(16).padStart(2,'0')}${parseInt(m[3]).toString(16).padStart(2,'0')}`;
            return '#00b31f';
          })()}
          onChange={(e) => setProp((props) => (props.backgroundColor = e.target.value))}
        />
      </FormControl>

      {/* Color de texto */}
      <FormControl fullWidth margin="normal">
        <FormLabel>Color de texto</FormLabel>
        <TextField type="color"
          value={textColor && textColor.startsWith('#') ? textColor : '#ffffff'}
          onChange={(e) => setProp((props) => (props.textColor = e.target.value))}
        />
      </FormControl>

      {/* Tamaño */}
      <FormControl fullWidth margin="normal">
        <FormLabel>Tamaño</FormLabel>
        <TextField select value={size || 'medium'}
          onChange={(e) => setProp((props) => (props.size = e.target.value))}
          SelectProps={{ native: true }}
        >
          <option value="small">Pequeño</option>
          <option value="medium">Mediano</option>
          <option value="large">Grande</option>
        </TextField>
      </FormControl>

      {/* rawStyle */}
      {rawStyle && Object.keys(rawStyle).length > 0 && (
        <FormControl fullWidth margin="normal">
          <FormLabel>Estilos heredados (CSS)</FormLabel>
          <Typography className={classes.hint}>Las props de arriba tienen prioridad.</Typography>
          <textarea
            className={classes.rawStyleTextarea}
            defaultValue={rawStyleStr}
            onBlur={(e) => {
              try { setProp((props) => (props.rawStyle = JSON.parse(e.target.value))); }
              catch (_) {}
            }}
            spellCheck={false}
            style={{ marginTop: 8 }}
          />
        </FormControl>
      )}
    </div>
  );
};

Button.craft = {
  props: {
    text:            'Hacer clic',
    className:       '',
    backgroundColor: '#00b31f',
    textColor:       '#ffffff',
    size:            'medium',
    rawStyle:        {},
  },
  related: { settings: ButtonSettings },
};